// src/pages/WelcomeTourPage.tsx
//
// Native-app first screen: the role question, straight away (founder, 1.5.1
// #1 — download to a QR in Apple Wallet in 10 taps). The wordmark, "Get Paid
// for Song Requests" and the two role cards: "I perform" goes straight to
// sign-up (/register), "I'm a fan" to the fan claim screen. The four info
// slides and the Get Started step in front of this screen are gone — people
// who download already know what we do.
//
// Home redirects logged-out NATIVE users here; the page also renders at
// /welcome on the web, but nothing links to it there.
import { useContext, useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FAN_MODE_PARKED, setAppRole } from "../lib/appRole";
import { selectPath, trackOnce } from "../lib/analytics";
import "./WelcomeTourPage.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

export default function WelcomeTourPage() {
  const { isAuthenticated, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Growth funnel: on native this screen IS the landing page.
  useEffect(() => {
    trackOnce("landing_viewed", "welcome_tour", { screen: "welcome_tour" });
  }, []);

  // Signed-in talent has no business on the intro — same guard as Home.
  if (!loading && isAuthenticated) {
    return <Navigate to="/requests" replace />;
  }

  // Always ask (founder, 1.4.1): remembering "dj" only ever saved one tap,
  // and it left a DJ phone with no way back to fan mode short of a
  // reinstall. The Back arrow on the fan claim screen and "Switch to DJ
  // mode" on fan home land here (/welcome?role=1 — the same screen), so
  // changing your mind is one tap. Logged-in talent never reach this page.
  const pickDj = () => {
    setAppRole("dj");
    selectPath("dj", "role_screen");
    navigate("/register");
  };

  const pickFan = () => {
    setAppRole("fan");
    selectPath("fan", "role_screen");
    // The claim screen (build 41; one fan headline since 1.5.1 #15,
    // FanJoinHead) with the three sign-in doors and Skip. Not required:
    // requesting a song never needs an account, and Apple 5.1.1 forbids
    // forcing one.
    navigate("/fan/claim");
  };

  return (
    <div className="wt-page">
      <Link to="/login" className="wt-login-link">{tr("welcomeTourPage.logIn")}</Link>
      <div className="wt-role">
        <img
          src="/request-wordmark.png"
          alt={tr("welcomeTourPage.requestApp")}
          className="wt-role-logo wt-role-in wt-role-d1"
        />
        <h1 className="wt-role-title wt-role-in wt-role-d2">
          <span className="hero-shimmer"><T k="welcomeTourPage.getPaidForSong2" tags={{ 0: <br /> }} /></span>
        </h1>
        <button
          type="button"
          className="wt-role-card wt-role-card--dj wt-role-in wt-role-d3"
          onClick={pickDj}
        >
          <span className="wt-role-emoji" aria-hidden>🎧</span>
          <span className="wt-role-name">{tr("welcomeTourPage.iPerform")}</span>
          <span className="wt-role-sub">{tr("welcomeTourPage.getPaidForSong")}</span>
        </button>
        {/* Fan mode parked (lib/appRole): the talent card only. */}
        {!FAN_MODE_PARKED && (
          <button
            type="button"
            className="wt-role-card wt-role-in wt-role-d4"
            onClick={pickFan}
          >
            <span className="wt-role-emoji" aria-hidden>🎉</span>
            <span className="wt-role-name">{tr("welcomeTourPage.iMAFan")}</span>
            <span className="wt-role-sub">
              {tr("welcomeTourPage.requestSongsPlaylistsTrack")}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
